import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import utils from '../../universal/utils';
import adminFetch from '../../universal/adminFetch';
import useIsMountedRef from '../../hooks/useIsMountedRef';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  status: {
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  winnerCell: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  metricCell: {
    fontFamily: 'monospace',
  },
  periodRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

const PERIOD_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

function pct(numerator, denominator) {
  if (!denominator || denominator === 0) return '—';
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

function fmt(n) {
  if (n == null) return '—';
  return typeof n === 'number' ? n.toLocaleString() : String(n);
}

function formatPageBreakdown(pages = []) {
  if (!Array.isArray(pages) || pages.length === 0) return '—';
  return pages
    .slice(0, 4)
    .map((page) => `${page.path || page.pageKey}: ${fmt(page.impressions)}`)
    .join(' · ');
}

export default function TemplateAnalytics({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/templates/analytics/adpass/${userId}?days=${days}`
      );
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load analytics');
      if (!isMountedRef.current) return;
      setRows(Array.isArray(data.metrics) ? data.metrics : []);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId, days]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const bestSteamCtr = rows.length > 1
    ? Math.max(...rows.map((r) => r.uniqueImpressions > 0 ? r.uniqueSteamClickers / r.uniqueImpressions : 0))
    : null;

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box className={classes.titleRow}>
          <TrendingUpIcon color="primary" />
          <Box>
            <Typography variant="h6">Template Analytics</Typography>
            <Typography variant="body2" color="textSecondary">
              Per-template impressions, SSO click-through rates, and signup completion.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {/* Period selector */}
        <Box className={classes.periodRow}>
          {PERIOD_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              size="small"
              variant={days === opt.value ? 'contained' : 'outlined'}
              color={days === opt.value ? 'primary' : 'default'}
              onClick={() => setDays(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
          <Button size="small" variant="outlined" onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={14} /> : 'Refresh'}
          </Button>
        </Box>

        {error ? (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {loading ? (
          <Box mt={3} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            No data yet. Events will appear once visitors start viewing public pages with a
            template assigned.
          </Typography>
        ) : (
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Template</strong></TableCell>
                  <TableCell align="right"><strong>Impressions</strong></TableCell>
                  <TableCell align="right"><strong>Unique impressions</strong></TableCell>
                  <TableCell align="right"><strong>Public page views</strong></TableCell>
                  <TableCell><strong>Top public pages</strong></TableCell>
                  <TableCell align="right"><strong>Steam CTR</strong></TableCell>
                  <TableCell align="right"><strong>Google CTR</strong></TableCell>
                  <TableCell align="right"><strong>Avg scroll depth</strong></TableCell>
                  <TableCell align="right"><strong>Signup rate</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const steamCtr = row.uniqueImpressions > 0
                    ? row.uniqueSteamClickers / row.uniqueImpressions
                    : 0;
                  const isTopSteam = bestSteamCtr !== null && rows.length > 1 && steamCtr === bestSteamCtr && bestSteamCtr > 0;

                  return (
                    <TableRow key={row.template} hover>
                      <TableCell>
                        <Typography variant="body2">
                          <strong>{row.template}</strong>
                        </Typography>
                      </TableCell>
                      <TableCell align="right" className={classes.metricCell}>
                        {fmt(row.impressions)}
                      </TableCell>
                      <TableCell align="right" className={classes.metricCell}>
                        {fmt(row.uniqueImpressions)}
                      </TableCell>
                      <TableCell align="right" className={classes.metricCell}>
                        {fmt(row.publicPageViews)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {formatPageBreakdown(row.pages)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        className={isTopSteam ? classes.winnerCell : classes.metricCell}
                      >
                        {pct(row.uniqueSteamClickers, row.uniqueImpressions)}
                        {isTopSteam ? ' ↑' : ''}
                      </TableCell>
                      <TableCell align="right" className={classes.metricCell}>
                        {pct(row.uniqueGoogleClickers, row.uniqueImpressions)}
                      </TableCell>
                      <TableCell align="right" className={classes.metricCell}>
                        {row.avgScrollDepth != null
                          ? `${Math.round(row.avgScrollDepth)}%`
                          : '—'}
                      </TableCell>
                      <TableCell align="right" className={classes.metricCell}>
                        {pct(row.uniqueCompleters, row.uniqueImpressions)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Paper>
  );
}
